/**
 * `NotFound` — what a member reads at an address that does not exist.
 *
 * The app had no answer for one: the edge serves the SPA shell for any
 * unknown path, so the client router received control and matched nothing,
 * and `RouteRegistry` emitted no fallback route. The behaviour was undefined
 * rather than chosen — for every typo, and for any address the app stops
 * serving. This surface is that choice, made once for ALL unmatched
 * addresses rather than per URL; a MOVED address is a different question and
 * keeps its own redirect descriptor (`/account → /profile`, D-048).
 *
 * Posture mirrors `content/routes/ContentUnavailable.tsx` exactly, because
 * this is the same kind of surface and inventing a second shape for it would
 * be the drift the prose routes already avoid: a single `Card` inside the
 * shell's `<main>` landmark, headline + body prose + one `react-router`
 * `Link` back to `/` so the reader escapes without a full-page reload.
 *
 * `routes/AdminForbidden.tsx` is the third member of that family and shares
 * everything BUT the link: it deliberately renders no interactive chrome and
 * points its reader back to the shell's `PrimaryNav` in prose instead. This
 * surface follows `ContentUnavailable` on that one point, because the
 * owner-approved copy reuses its `backHome` string (below) — the affordance
 * and the words that name it arrive together.
 *
 * No illustration, no status code on screen, no search box — a not-found
 * DEFAULT, not a not-found feature.
 *
 * Copy: the two visible strings are member-facing and therefore the owner's,
 * under the `common.notFound.*` scope (FR-040, Finnish-first). The link
 * REUSES the approved `content.unavailable.backHome` ("Palaa etusivulle")
 * rather than coining a second way to say the same thing — one term, one
 * thing, even across two surfaces.
 */

import type { JSX } from 'react';
import { Link } from 'react-router';

import { useTranslate } from '../i18n/useTranslate.js';
import { Card } from '../primitives/Card.js';

const pageClasses = 'flex flex-col gap-4';
const headingClasses = 'm-0 text-xl font-semibold';
const bodyClasses = 'm-0 text-base text-foreground';
const linkClasses = 'self-start text-sm font-medium text-primary underline';

export function NotFound(): JSX.Element {
  const t = useTranslate();

  return (
    <Card className={pageClasses}>
      <h2 className={headingClasses}>{t('common.notFound.heading')}</h2>
      <p className={bodyClasses}>{t('common.notFound.body')}</p>
      <Link className={linkClasses} to="/">
        {t('content.unavailable.backHome')}
      </Link>
    </Card>
  );
}
